import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import styled from '@emotion/styled';
import moment from 'moment';
import { Button, Layout, Typography, message } from '@robinpowered/ui-kit';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@awesome.me/kit-cf858715fd/icons/classic/solid';
import {
  BufferEventType,
  ComposerAnalyticsEvent,
  ComposerContainerMode,
  ComposerMode,
  useEmitEventDetailsActionError,
  useEmitEventDetailsActionSuccess,
  useEmitEventDetailsDeleteRequested,
  useEmitEventSubmitError,
  useEmitEventSubmitSuccess,
  EventComposer,
  EventComposerConfig,
  type EventComposerFormValues,
  type EventComposerHandle,
  EventDataResponse,
  EventSubmitMeta,
  EventDetailsContent,
  type EventDetailsActionMeta,
  EventDetailsSuccessAction,
  EventDetailsSuccessActions,
  EventToBeCreated,
  TimeFormatsConstants,
  type TimeFormats,
  useEventComposerConfig,
  useTrackComposerEvent,
} from '@robinpowered/dashboard-apps-patterns';
import {
  clearCreateEventParams,
  COMPOSER_SPACE_PREVIEW_PARAM,
  useEmitEventDraftPatch,
  type EventDraftPatch,
  type EventDraftSpaceBuffers,
  type EventParamMode,
  formatEventParam,
  getComposerSpacePreviewId,
  isAgentChatOpen,
  parseEventParam,
  replaceSharedUrl,
  useEventDraftPatch,
  useFeatureFlags,
  useGetUserSetting,
  useSiderOccupancy,
  useSiderRequestForeground,
} from '@robinpowered/dashboard-apps-common';
import { getActiveAppNameForPathname } from '../../single-spa-config';
import { EventOverlayHostLegacy } from './EventOverlayHostLegacy';
import { useAdaptedUserAndOrg } from './useAdaptedUserAndOrg';
import { useTranslation } from 'react-i18next';
import { useApolloContext, useAuthContext, useAmplitude } from 'contexts';
import { Sentry } from 'lib/sentry';
import { config } from 'config';

const { Sider } = Layout;

// Matches the office space-details sider width (340px) so the composer opens
// exactly in place of it — same rectangle, no layout shift — rather than being
// 40px wider and nudging the content when it replaces an MFE's right sider.
// TODO: update siders to be 340px (across all apps?).
const SIDER_WIDTH = 340;

/**
 * Origin tag the shell stamps on its outgoing draft patches. Mirrored on
 * the subscriber's `ignoreOrigin` so the composer doesn't react to its own
 * echoes. MFEs that listen for shell-originated patches should match on
 * this exact string.
 */
const SHELL_COMPOSER_ORIGIN = 'shell-composer';

/**
 * Serialize the composer form's per-space buffer durations into the wire
 * shape (`EventDraftPatch.spaceBuffers`): keyed by stringified space id,
 * ISO durations, sides without a duration omitted.
 *
 * The channel carries RAW meeting times plus this map — never
 * buffer-expanded times. An earlier design expanded the primary space's
 * buffers into `startTimeISO`/`endTimeISO` on emit ("block extent"
 * semantics), but the inbound path wrote incoming times straight into the
 * form's raw meeting times with no inverse strip, so every grid-side move
 * re-expanded the already-expanded times and the draft grew without
 * bound. Receivers expand raw times with these durations at render time.
 *
 * Emitted on every echo as a FULL replacement of the current form state —
 * `{}` means "no buffers" and clears anything a receiver mirrored earlier.
 */
const serializeSpaceBuffers = (
  values: EventComposerFormValues
): EventDraftSpaceBuffers => {
  const result: EventDraftSpaceBuffers = {};
  for (const [spaceId, buffers] of Object.entries(values.spaceBuffers ?? {})) {
    if (!buffers?.beforeEvent && !buffers?.afterEvent) continue;
    result[spaceId] = {
      ...(buffers.beforeEvent ? { beforeISO: buffers.beforeEvent } : {}),
      ...(buffers.afterEvent ? { afterISO: buffers.afterEvent } : {}),
    };
  }
  return result;
};

/**
 * Shell-level right-hand sidebar that hosts `EventDetails` or `EventComposer`
 * from `@robinpowered/dashboard-apps-patterns`. Visibility and contents are
 * derived from the canonical `event` query param (see
 * `common/common/src/utilities/eventParam.ts`).
 *
 * The Sider is a layout primitive that sits next to the MFE mount container;
 * opening/closing it never mutates the pathname, so the underlying MFE is
 * unaffected.
 */
export const EventOverlayHost = (): JSX.Element | null => {
  const { checkFlag, loading } = useFeatureFlags();
  const shellEventComposerEnabled =
    checkFlag('hydra(shell-event-composer)') && !loading;

  // Feature-flag gate at the root: when the shell-event-composer flow is
  // off, render the prior implementation untouched. The body below is the
  // new (shell-hosted composer) variant.
  if (!shellEventComposerEnabled) {
    return <EventOverlayHostLegacy />;
  }

  return <EventOverlayHostShellComposer />;
};

const EventOverlayHostShellComposer = (): JSX.Element | null => {
  const [searchParams] = useSearchParams();
  const { tenantId } = useApolloContext();
  const { currentUser, currentOrg } = useAuthContext();
  const { trackEvent } = useAmplitude();
  const [, contextHolder] = message.useMessage();
  const emitEventDraftPatch = useEmitEventDraftPatch();
  const emitEventSubmitSuccess = useEmitEventSubmitSuccess();
  const emitEventSubmitError = useEmitEventSubmitError();
  const emitEventDetailsActionError = useEmitEventDetailsActionError();
  const emitEventDetailsDeleteRequested = useEmitEventDetailsDeleteRequested();
  const emitEventDetailsActionSuccess = useEmitEventDetailsActionSuccess();

  const parsed = useMemo(
    () => parseEventParam(searchParams.get('event')),
    [searchParams]
  );
  const { mode, eventId } = parsed;
  const isOpen = mode !== null;

  // While the office previews a space (the "add to event" flow), the composer
  // is backgrounded: kept MOUNTED so its in-progress form state survives and
  // its `addSpaceId` listener stays live, but collapsed to width 0 so the
  // office space-details sider owns the visible slot. Derived from the
  // `preview_space` query param the office sets.
  const backgrounded =
    getComposerSpacePreviewId(searchParams.toString()) !== null;

  // Whether an MFE right-hand sider currently occupies the slot (reported over
  // the sider-occupancy channel — office's space/desk details, schedule's
  // workweek day sider). Presentation only: when the slot is occupied we swap
  // the composer in instantly (a handoff between two equal-width siders, so
  // animating would wobble the surrounding content); when it's empty we slide
  // the composer in (schedule's meeting-rooms grid, which matches the Drawer
  // slide it had before). Occupancy is stable while the composer is open, so
  // `animateSlide` governs both the open and close transitions.
  //
  // Tracked as a set of occupant ids rather than a single boolean so it's
  // robust to mount/unmount ordering across apps: navigating office → workweek
  // may deliver the new occupant's `true` before the old one's `false`, and a
  // single boolean would end up stale. Each emitter reports `false` on unmount.
  const occupiedIdsRef = useRef<Set<string>>(new Set());
  const [siderOccupied, setSiderOccupied] = useState(false);
  useSiderOccupancy((id, occupied) => {
    const ids = occupiedIdsRef.current;
    if (occupied) {
      ids.add(id);
    } else {
      ids.delete(id);
    }
    setSiderOccupied(ids.size > 0);
  });

  // The shell assistant panel participates in the swap policy too. It does
  // report occupancy, but that report lands in an effect AFTER the commit in
  // which this sider yields to it — the Assistant toggle sets the `agent`
  // param and asks this composer to close synchronously in the same click, so
  // by the time the assistant's occupancy arrives this sider has already
  // collapsed (animated). The `agent` param itself, though, IS on the URL in
  // every commit where this sider's width changes as part of an assistant
  // handoff — yielding to the assistant, opening over a backgrounded one, or
  // closing to reveal it — so gate on the param directly.
  const agentChatOpen = isAgentChatOpen(searchParams.toString());
  const animateSlide = !siderOccupied && !agentChatOpen;

  // Stable, composer-shaped user/org — see useAdaptedUserAndOrg for the
  // identity-stability rationale.
  const { adaptedUser, adaptedOrg } = useAdaptedUserAndOrg(
    currentUser,
    currentOrg
  );

  const composerConfig = useEventComposerConfig({
    tenantId,
    currentUser: adaptedUser,
    currentOrg: adaptedOrg,
    sessionCookieName: config.sessionCookieName,
    urls: {
      featureFlagUrl: config.featureFlagUrl,
      integrationsServiceUrl: config.integrationsServiceUrl,
      apiUrl: config.apiUrl,
      baseApiUrl: config.baseApiUrl,
      dashboardUrl: config.dashboardUrl,
      imageServerUrl: config.imageServerUrl,
    },
  });

  /**
   * Mutate the `event` query param while preserving every other param and the
   * pathname. Pass `null` to remove the param entirely (close the sidebar).
   *
   * Goes through `replaceSharedUrl` rather than react-router's
   * `setSearchParams` because shell and each MFE mount their own
   * `BrowserRouter`. `setSearchParams` only notifies shell's Router — MFE
   * Routers (e.g. schedule, which mirrors the open composer in its grid)
   * would stay stale. `replaceSharedUrl` dispatches a synthetic popstate so
   * every Router on the page picks the change up. Switching to clear-mode
   * (`null`) also strips every create-composer param (seed *and* prefill like
   * `title` / `invitee_count`) so a closed sidebar never leaves stale state
   * that the next create open would inherit.
   *
   * Rebuild from the *live* `window.location`, not this Router's `searchParams`
   * snapshot. MFEs write their own params (e.g. office's `csr` selected
   * resource) with a bare `replaceState` that never notifies this Router, so
   * the snapshot can be missing params that are genuinely on the URL. Building
   * from the snapshot would silently drop them, and the synthetic popstate from
   * our write would then make the owning MFE reset the matching atom (its sider
   * goes blank). Reading `window.location` preserves whatever the active MFE
   * has committed.
   */
  const setEventParam = useCallback((next: string | null) => {
    const params = new URLSearchParams(window.location.search);
    if (next === null) {
      params.delete('event');
      clearCreateEventParams(params);
      // Defensive: a full close should never leave the transient space-preview
      // param dangling (it only makes sense while the composer is open).
      params.delete(COMPOSER_SPACE_PREVIEW_PARAM);
    } else {
      params.set('event', next);
    }
    const query = params.toString();
    replaceSharedUrl(`${window.location.pathname}${query ? `?${query}` : ''}`);
  }, []);

  const handleClose = useCallback(() => setEventParam(null), [setEventParam]);

  const handleClickEdit = useCallback(
    (id: string) => {
      setEventParam(formatEventParam({ mode: 'edit', eventId: id }));
    },
    [setEventParam]
  );

  const handleSubmit = useCallback(
    (
      event: EventDataResponse,
      composerMode: ComposerMode,
      meta?: EventSubmitMeta
    ) => {
      // Tell active MFEs the composer just succeeded so they can refresh
      // their data, fire amplitude events, show success toasts, etc. The
      // shell deliberately does NOT show its own toast — the surface the
      // user came from (e.g. schedule's meeting-rooms grid) owns the
      // post-submit UX so it stays consistent with the rest of that app.
      //
      // `originalEventId` captures the id the composer was opened on (EDIT
      // only). The fresh `event.id` may differ for recurring "this and
      // following" updates that mint a new series master, so subscribers
      // need both to update their local tree correctly.
      emitEventSubmitSuccess({
        event,
        composerMode,
        originalEventId:
          composerMode === ComposerMode.EDIT ? eventId ?? null : null,
        // e.g. an organizer-space move whose calendar sync is async —
        // subscribers adjust their post-submit polling per space.
        meta,
      });
      // Drop back to details mode on the (possibly new) event id.
      const id = String(event.id);
      setEventParam(formatEventParam({ mode: 'details', eventId: id }));
    },
    [eventId, setEventParam, emitEventSubmitSuccess]
  );

  const handleComposerError = useCallback(
    (error: Error, composerMode: ComposerMode) => {
      // Sentry capture is shell-side because observability is global. The
      // user-facing surfacing (toast / dialog) is forwarded to MFEs via
      // the lifecycle channel — see the rationale on `handleSubmit`.
      Sentry.captureException(error);
      emitEventSubmitError({
        message: error.message || 'An error occurred',
        composerMode,
      });
    },
    [emitEventSubmitError]
  );

  /**
   * Errors from the read-only `EventDetailsContent` flow (load failures,
   * action errors like delete / approve / reject). Same split as
   * composer errors: shell captures to Sentry (global observability) and
   * forwards a user-facing payload on the details lifecycle channel so
   * each MFE can render the toast in its own style.
   *
   * `eventId` is sourced from the URL — `EventDetailsContent`'s `onError`
   * doesn't carry it, but at any moment the details panel is rendered we
   * know which event id it was opened on.
   */
  const handleDetailsError = useCallback(
    (error: Error) => {
      Sentry.captureException(error);
      if (!eventId) return;
      emitEventDetailsActionError({
        message: error.message || 'An error occurred',
        eventId,
      });
    },
    [eventId, emitEventDetailsActionError]
  );

  /**
   * Fires as soon as the user confirms delete in the details panel, BEFORE
   * the API call resolves. MFEs use this to clean up optimistic state and
   * start sync polling so the chip reflects the in-flight delete state.
   */
  const handleDetailsDeleteRequested = useCallback(() => {
    if (!eventId) return;
    emitEventDetailsDeleteRequested({ eventId });
  }, [eventId, emitEventDetailsDeleteRequested]);

  /**
   * Fires after any details action (approve / reject / delete / check-in /
   * remove-space-invitee / moved-organizer-space) succeeds. The shell
   * auto-closes for DELETE_EVENT (the event no longer exists) and forwards
   * the action to MFEs so they can show the matching success toast.
   *
   * `meta` rides along untouched — MOVED_ORGANIZER_SPACE carries
   * `meta.organizerMove` so grid hosts can start their move_out/create sync
   * polling on the 202.
   */
  const handleDetailsActionSuccess = useCallback(
    (action: EventDetailsSuccessAction, meta?: EventDetailsActionMeta) => {
      if (eventId) {
        emitEventDetailsActionSuccess({ action, eventId, meta });
      }
      if (action === EventDetailsSuccessActions.DELETE_EVENT) {
        handleClose();
      }
    },
    [eventId, handleClose, emitEventDetailsActionSuccess]
  );

  const handleTrackBufferEvent = useCallback(
    (eventType: BufferEventType, metadata?: Record<string, unknown>) => {
      // Buffer event names mirror schedule's mapping (kept in sync manually
      // until we share an Amplitude event registry).
      const amplitudeEventName: Record<BufferEventType, string> = {
        addBuffer: 'added_buffer',
        removeBuffer: 'removed_buffer',
        createdBufferEvent: 'created_buffer_event',
        detectedBufferConflict: 'detected_buffer_conflict',
      };
      trackEvent(amplitudeEventName[eventType], metadata);
    },
    [trackEvent]
  );

  // Entry point = the MFE currently mounted under the composer, answering
  // "where do users open the composer?". Resolved from the single-spa
  // registry's `activeWhen` predicates — the same source of truth that
  // decides which app is mounted — rather than assuming URL shape.
  const { pathname } = useLocation();
  const entryPoint = getActiveAppNameForPathname(pathname) ?? 'unknown';
  const handleTrackComposerEvent = useTrackComposerEvent({
    trackEvent,
    entryPoint,
  });

  const selectedTimezone = currentUser?.timeZone || moment.tz.guess();

  // Mirror the schedule app's locale logic: read the user's "24-hour-time"
  // setting and map to the corresponding TimeFormats enum value. Skips until
  // both userId and tenantId are present; until then we fall back to the
  // 12-hour default (matches the schedule LocalizationContext default).
  const { data: hourFormatData } = useGetUserSetting(
    currentUser?.id,
    tenantId,
    '24-hour-time'
  );
  const timeFormat: TimeFormats = useMemo(() => {
    const raw = hourFormatData?.getUserSetting?.value;
    if (!raw) return TimeFormatsConstants.TWELVE_HOUR;
    try {
      return JSON.parse(raw) === true
        ? TimeFormatsConstants.TWENTY_FOUR_HOUR
        : TimeFormatsConstants.TWELVE_HOUR;
    } catch {
      return TimeFormatsConstants.TWELVE_HOUR;
    }
  }, [hourFormatData]);

  // The "initial" event for CREATE mode — derived from URL prefill query
  // params. Used as the immutable reference for EventComposer's
  // `initialEvent` prop, which it reads to seed react-hook-form's defaults.
  //
  // Dependencies are deliberately the individual prefill *param strings*,
  // NOT `searchParams` as an object. MFE-side URL pushes (e.g. schedule's
  // `currentDate` updates) bubble up to shell's router and would otherwise
  // change `searchParams` identity, re-derive `initialEvent`, and fire the
  // `setCreateEvent(initialEvent)` reset effect below — clobbering the
  // user's in-progress date pick because `initialEvent` is always built
  // from URL seeds (which never include the user's mid-edit changes).
  //
  // Reading each param value as a primitive string and listing those as
  // deps means unrelated URL params (currentDate, locationId, …) flipping
  // doesn't invalidate `initialEvent`.
  const startedAtParam = searchParams.get('started_at');
  const endedAtParam = searchParams.get('ended_at');
  const spaceIdParam = searchParams.get('space_id');
  const levelIdParam = searchParams.get('level_id');
  const isAllDayParam = searchParams.get('is_all_day');
  const isPrivateParam = searchParams.get('is_private');
  const inviteeCountParam = searchParams.get('invitee_count');
  const titleParam = searchParams.get('title');
  const eventUidParam = searchParams.get('event_uid');
  const timezoneParam = searchParams.get('timezone');
  const initialEvent = useMemo<EventToBeCreated | null>(() => {
    if (mode !== 'create') return null;
    return buildInitialCreateEvent(
      {
        timezone: timezoneParam,
        startedAt: startedAtParam,
        endedAt: endedAtParam,
        spaceId: spaceIdParam,
        levelId: levelIdParam,
        isAllDay: isAllDayParam,
        isPrivate: isPrivateParam,
        inviteeCount: inviteeCountParam,
        title: titleParam,
        eventUid: eventUidParam,
      },
      selectedTimezone
    );
  }, [
    mode,
    timezoneParam,
    startedAtParam,
    endedAtParam,
    spaceIdParam,
    levelIdParam,
    isAllDayParam,
    isPrivateParam,
    inviteeCountParam,
    titleParam,
    eventUidParam,
    selectedTimezone,
  ]);

  // Controlled CREATE-mode event state. EventComposer treats its `event` prop
  // as the source of truth for the date — it runs a sync effect that snaps
  // the form's date back to `event.date` any time they diverge (see
  // common/patterns/.../CreateEventContext.tsx). If we keep passing the
  // stable `initialEvent` for `event`, every date pick gets reverted on the
  // next render. So we mirror the form values back into state via
  // `onEventChange`, and pass that state as `event`.
  const [createEvent, setCreateEvent] = useState<EventToBeCreated | null>(null);
  useEffect(() => {
    // (Re)seed the controlled state whenever the URL-derived `initialEvent`
    // changes — i.e. when we enter CREATE mode or when a prefill param
    // changes. Leaving CREATE mode resets to null so the next entry starts
    // fresh instead of inheriting the previous draft.
    setCreateEvent(initialEvent);
  }, [initialEvent]);

  const handleCreateEventChange = useCallback(
    (values: EventComposerFormValues) => {
      setCreateEvent((prev) => {
        if (!prev) return prev;
        // Only echo back the fields the composer actually syncs from the
        // parent — currently just `date`. Title/startTime/endTime/etc. are
        // owned by react-hook-form internally and don't have a sync-from-
        // parent effect, so we don't need to mirror them here. If patterns
        // adds new sync effects (e.g. for startTime), extend this.
        if (prev.date.isSame(values.date)) return prev;
        return { ...prev, date: values.date };
      });
      // Mirror the live composer values onto the cross-MFE channel so
      // surfaces in other apps (e.g. schedule's meeting-rooms block) can
      // reflect the in-flight edit. `eventId: null` marks this as a
      // create-mode draft. The MFE that opened the composer is responsible
      // for correlating this back to its own state.
      // `dateISO` is emitted in addition to start/end because patterns-lib
      // updates `date` BEFORE `startTime` when the user picks a new date —
      // a subscriber that only watched startTimeISO would see the OLD
      // wall-clock day on the first onEventChange firing and miss the
      // navigation cue. Sending `date` explicitly lets receivers react on
      // patch 1 without races. See `EventDraftPatch.dateISO`.
      emitEventDraftPatch(
        {
          eventId: null,
          startTimeISO: values.startTime.toISOString(),
          endTimeISO: values.endTime.toISOString(),
          dateISO: values.date.toISOString(),
          // Ordered list of the event's current space ids (first is primary)
          // so surfaces like the office map can keep their highlight in sync
          // as spaces are added/removed in the composer.
          spaceIds: (values.spaces ?? []).map((space) => String(space.id)),
          // Raw times + explicit buffers; receivers render extents
          // themselves (see `serializeSpaceBuffers`).
          spaceBuffers: serializeSpaceBuffers(values),
        },
        SHELL_COMPOSER_ORIGIN
      );
    },
    [emitEventDraftPatch]
  );

  const handleEditEventChange = useCallback(
    (values: EventComposerFormValues) => {
      // EDIT mode mirrors live edits the same way CREATE mode does — the
      // difference is we have a concrete eventId to scope receivers to.
      // `eventId` is read from the URL above, not from the form, because
      // the form's `eventId` field may be empty in some transient states.
      if (!eventId) return;
      emitEventDraftPatch(
        {
          eventId,
          startTimeISO: values.startTime.toISOString(),
          endTimeISO: values.endTime.toISOString(),
          dateISO: values.date.toISOString(),
          // Ordered list of the event's current space ids (first is primary)
          // so surfaces like the office map can keep their highlight in sync
          // as spaces are added/removed in the composer.
          spaceIds: (values.spaces ?? []).map((space) => String(space.id)),
          // Raw times + explicit buffers; receivers render extents
          // themselves (see `serializeSpaceBuffers`).
          spaceBuffers: serializeSpaceBuffers(values),
        },
        SHELL_COMPOSER_ORIGIN
      );
    },
    [eventId, emitEventDraftPatch]
  );

  // Imperative handle into the composer's react-hook-form. Used below to
  // apply patches arriving from other MFEs.
  const composerRef = useRef<EventComposerHandle>(null);

  // Subscribe to draft patches from other MFEs (meeting-rooms drag/resize,
  // office surfaces, etc.) and push them into the composer's form. Our own
  // outgoing patches are stamped with SHELL_COMPOSER_ORIGIN and skipped by
  // `ignoreOrigin` so we don't react to our own echoes.
  useEventDraftPatch(
    useCallback(
      (patch: EventDraftPatch) => {
        // Only apply patches that target the open composer. For EDIT mode
        // that means the eventIds must match. For CREATE mode the inbound
        // patch must also carry `eventId: null` — anything else is a
        // foreign edit and would be misleading to display.
        if (mode === 'edit') {
          if (!eventId || patch.eventId !== eventId) return;
        } else if (mode === 'create') {
          if (patch.eventId !== null) return;
        } else {
          return;
        }
        const handle = composerRef.current;
        if (!handle) return;
        const startTime = patch.startTimeISO
          ? moment.tz(patch.startTimeISO, selectedTimezone)
          : undefined;
        handle.patchFormValues({
          startTime,
          endTime: patch.endTimeISO
            ? moment.tz(patch.endTimeISO, selectedTimezone)
            : undefined,
          // Keep the form's `date` field in step with the patched times.
          // Without this the composer keeps the old day, and its outgoing
          // echo (which carries `dateISO: values.date`) yanks date-synced
          // surfaces (e.g. schedule's workday grid) back to the stale day.
          date: startTime?.clone().startOf('day'),
        });
      },
      [mode, eventId, selectedTimezone]
    ),
    { ignoreOrigin: SHELL_COMPOSER_ORIGIN }
  );

  // Single-sider policy: when an MFE right-hand sider asks to take the slot,
  // yield it by closing our sider first. For the composer (create / edit) we
  // go through the imperative handle so the unsaved-changes discard guard
  // runs; the read-only details panel has no handle, so we close it directly.
  // The MFE's open proceeds once the `event` param clears — or is dropped if
  // the user backs out of the discard dialog (see `useSiderArbitration`).
  useSiderRequestForeground(
    useCallback(() => {
      if (!isOpen) return;
      const handle = composerRef.current;
      if (handle) {
        handle.requestClose();
      } else {
        handleClose();
      }
    }, [isOpen, handleClose])
  );

  // Single always-mounted Sider that toggles its `collapsed` state based on
  // the `event` query param. This mirrors `NavSidebar`'s pattern so the Sider
  // is a true flex sibling of `NavSidebar` and `MainContainer` in `AppShell`'s
  // Layout — it pushes the main content rather than floating over it.
  return (
    <>
      {contextHolder}
      <RightSider
        className={isOpen && !backgrounded ? 'composer-open' : undefined}
        collapsed={!isOpen || backgrounded}
        collapsedWidth={0}
        width={SIDER_WIDTH}
        trigger={null}
        reverseArrow
        // Width animation gate (see the note on RightSider below): inline
        // because antd's own `transition` lives on a class whose specificity/
        // injection order varies with the cssinjs config — a class override
        // can tie and lose, but an inline style always wins.
        style={{ transition: animateSlide ? undefined : 'none' }}
      >
        {isOpen
          ? renderBody({
              mode: mode as EventParamMode,
              eventId,
              backgrounded,
              composerConfig,
              initialEvent,
              createEvent,
              handleCreateEventChange,
              handleEditEventChange,
              composerRef,
              selectedTimezone,
              timeFormat,
              handleClose,
              handleClickEdit,
              handleSubmit,
              handleComposerError,
              handleDetailsError,
              handleDetailsDeleteRequested,
              handleDetailsActionSuccess,
              handleTrackBufferEvent,
              handleTrackComposerEvent,
            })
          : null}
      </RightSider>
    </>
  );
};

// ---------------------------------------------------------------------------
// Render helpers
// ---------------------------------------------------------------------------

type RenderArgs = {
  mode: EventParamMode;
  eventId: string | null;
  /**
   * True while the composer is kept mounted but collapsed for the office
   * space-preview flow. Forwarded so the composer closes its portaled
   * drawers (add space / suggested times), which would otherwise float
   * over the office details sider that took the slot.
   */
  backgrounded: boolean;
  composerConfig: EventComposerConfig | undefined;
  /** URL-derived event for CREATE mode — stable, used for `initialEvent`. */
  initialEvent: EventToBeCreated | null;
  /** Controlled event state for CREATE mode — updated via onEventChange. */
  createEvent: EventToBeCreated | null;
  handleCreateEventChange: (values: EventComposerFormValues) => void;
  handleEditEventChange: (values: EventComposerFormValues) => void;
  composerRef: React.RefObject<EventComposerHandle>;
  selectedTimezone: string;
  timeFormat: (typeof TimeFormatsConstants)[keyof typeof TimeFormatsConstants];
  handleClose: () => void;
  handleClickEdit: (id: string) => void;
  handleSubmit: (
    event: EventDataResponse,
    composerMode: ComposerMode,
    meta?: EventSubmitMeta
  ) => void;
  handleComposerError: (error: Error, composerMode: ComposerMode) => void;
  handleDetailsError: (error: Error) => void;
  handleDetailsDeleteRequested: () => void;
  handleDetailsActionSuccess: (
    action: EventDetailsSuccessAction,
    meta?: EventDetailsActionMeta
  ) => void;
  handleTrackBufferEvent: (
    eventType: BufferEventType,
    metadata?: Record<string, unknown>
  ) => void;
  handleTrackComposerEvent: (
    eventType: ComposerAnalyticsEvent,
    metadata?: Record<string, unknown>
  ) => void;
};

const renderBody = ({
  mode,
  eventId,
  backgrounded,
  composerConfig,
  initialEvent,
  createEvent,
  handleCreateEventChange,
  handleEditEventChange,
  composerRef,
  selectedTimezone,
  timeFormat,
  handleClose,
  handleClickEdit,
  handleSubmit,
  handleComposerError,
  handleDetailsError,
  handleDetailsDeleteRequested,
  handleDetailsActionSuccess,
  handleTrackBufferEvent,
  handleTrackComposerEvent,
}: RenderArgs): JSX.Element | null => {
  if (!composerConfig) {
    // Auth / tenant / token still loading. The Sider is open per the URL but
    // the inner components can't render until config resolves; render null.
    return null;
  }

  if (mode === 'details') {
    if (!eventId) return null;
    return (
      <SiderPanelHeader onClose={handleClose}>
        <EventDetailsContent
          config={composerConfig}
          eventId={eventId}
          timezone={selectedTimezone}
          timeFormat={timeFormat}
          onClose={handleClose}
          onClickEdit={handleClickEdit}
          onDelete={handleDetailsDeleteRequested}
          onError={handleDetailsError}
          onSuccess={handleDetailsActionSuccess}
          onTrackComposerEvent={handleTrackComposerEvent}
        />
      </SiderPanelHeader>
    );
  }

  if (mode === 'edit') {
    if (!eventId) return null;
    return (
      <EventComposer
        composerMode={ComposerMode.EDIT}
        containerMode={ComposerContainerMode.INLINE}
        backgrounded={backgrounded}
        config={composerConfig}
        event={{ id: eventId }}
        composerRef={composerRef}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onError={handleComposerError}
        onEventChange={handleEditEventChange}
        timeFormat={timeFormat}
        selectedTimezone={selectedTimezone}
        onTrackBufferEvent={handleTrackBufferEvent}
        onTrackComposerEvent={handleTrackComposerEvent}
      />
    );
  }

  if (mode === 'create') {
    // `initialEvent` is the URL-derived starting point; `createEvent` is the
    // controlled state that follows user edits via `onEventChange`. Both must
    // be present — the controlled state is seeded from `initialEvent` in an
    // effect, so on the very first render after entering CREATE mode they
    // briefly differ and the composer can't render yet.
    if (!initialEvent || !createEvent) return null;
    return (
      <EventComposer
        composerMode={ComposerMode.CREATE}
        containerMode={ComposerContainerMode.INLINE}
        backgrounded={backgrounded}
        config={composerConfig}
        initialEvent={initialEvent}
        event={createEvent}
        composerRef={composerRef}
        onEventChange={handleCreateEventChange}
        onClose={handleClose}
        onSubmit={handleSubmit}
        onError={handleComposerError}
        timeFormat={timeFormat}
        selectedTimezone={selectedTimezone}
        onTrackBufferEvent={handleTrackBufferEvent}
        onTrackComposerEvent={handleTrackComposerEvent}
      />
    );
  }

  return null;
};

/**
 * Strings extracted from URL query params. Passing primitives lets the
 * caller list them individually as `useMemo` deps so the result identity
 * only changes when one of these actual seed values changes — not on
 * every `searchParams` identity flip.
 */
type CreateEventPrefillParams = {
  timezone: string | null;
  startedAt: string | null;
  endedAt: string | null;
  spaceId: string | null;
  levelId: string | null;
  isAllDay: string | null;
  isPrivate: string | null;
  inviteeCount: string | null;
  title: string | null;
  eventUid: string | null;
};

const buildInitialCreateEvent = (
  prefill: CreateEventPrefillParams,
  selectedTimezone: string
): EventToBeCreated => {
  const tz = prefill.timezone || selectedTimezone;

  const startTime = prefill.startedAt
    ? moment.tz(prefill.startedAt, tz)
    : moment().tz(tz);
  const endTime = prefill.endedAt
    ? moment.tz(prefill.endedAt, tz)
    : startTime.clone().add(30, 'minutes');

  return {
    date: startTime.clone(),
    startTime,
    endTime,
    title: prefill.title ?? undefined,
    isAllDayEvent: prefill.isAllDay === 'true' || prefill.isAllDay === '1',
    isPrivate: prefill.isPrivate === 'true' || prefill.isPrivate === '1',
    uid: prefill.eventUid ?? undefined,
    attendeeCount: prefill.inviteeCount
      ? Number(prefill.inviteeCount)
      : undefined,
    spaceId: prefill.spaceId ?? undefined,
    levelId: prefill.levelId ? Number(prefill.levelId) : undefined,
  };
};
/**
 * Header row that the shell renders above an `EventDetailsContent` body, since
 * the patterns component no longer carries its own Drawer title bar in inline
 * mode. Title text comes from the patterns translation namespace
 * (`meetingRooms` / `eventDetails.title`) to match the Drawer variant.
 */
const SiderPanelHeader = ({
  onClose,
  children,
}: {
  onClose: () => void;
  children: React.ReactNode;
}): JSX.Element => {
  const { t } = useTranslation('meetingRooms');
  return (
    <>
      <PanelHeader>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('eventDetails.title', 'Event details')}
        </Typography.Title>
        <Button
          type="text"
          shape="default"
          size="middle"
          icon={<FontAwesomeIcon icon={faXmark} />}
          onClick={onClose}
          aria-label="Close"
        />
      </PanelHeader>
      <PanelBody>{children}</PanelBody>
    </>
  );
};

const RightSider = styled(Sider)`
  background: var(--ant-color-bg-container, #ffffff);
  border-left: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  height: 100%;
  overflow: hidden;
  /* Width animation is gated by an inline style on the element (driven by MFE
     sider occupancy — see EventOverlayHost's animateSlide):
       - occupied slot (e.g. office handing off from space/desk details): swap
         INSTANTLY by killing the transition. The composer and the office sider
         are the same width, so an instant swap leaves the map exactly in
         place; animating would drag it sideways.
       - empty slot (e.g. schedule's meeting-rooms grid): leave antd's built-in
         Sider transition in place so the sider slides in/out at the standard
         antd duration, restoring the slide the composer had as a Drawer before
         it moved into this shell Sider.
     Inline rather than a class rule here: antd's own transition lives on a
     class whose specificity/injection order varies with the cssinjs config —
     a class override can tie and lose, but an inline style always wins. */
  @keyframes composerContentFadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
  /* Inner flex column so the panel header + body stack vertically. Content width
     is pinned to the full sider width so it doesn't reflow while the sider width
     animates — the sider clips it (overflow: hidden) as it expands, giving a
     clean slide-in reveal rather than reflowing text. */
  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
    min-height: 0;
    height: 100%;
    width: ${SIDER_WIDTH}px;
    transition: none;
  }
  /* Fade the contents in each time the composer opens (the class is added on
     open and removed on close, so the animation re-triggers per open). */
  &.composer-open .ant-layout-sider-children {
    animation: composerContentFadeIn 0.18s ease-out;
  }
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--ant-color-border-secondary, #f0f0f0);
  flex-shrink: 0;
`;

const PanelBody = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;
